import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik, getIn } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Col, Offcanvas, Row } from "react-bootstrap";
import * as Yup from "yup";

import {
  CountryWithCode,
  RelevantClauseOptions,
  StateWithCode
} from "../../../../config/filingConstants";
import { ISection80GGADonation } from "../../../../config/interfaces";
import {
  Section80GGADonationFormInitValues,
  Section80GGADonationFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";

type Props = {
	showCanvas: boolean;
	formValues: ISection80GGADonation | null;
	handleCloseBtnPress: (salaryData: ISection80GGADonation | null) => void;
};

const DonationUnderSection80GGAForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress, formValues } = props;

	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [initFormValues, setInitFormValues] = useState<ISection80GGADonation>(
		Section80GGADonationFormInitValues
	);

	useEffect(() => {
		if (showCanvas && formValues) {
			setInitFormValues(() => cloneDeep(formValues));
		} else {
			setInitFormValues(() => cloneDeep(Section80GGADonationFormInitValues));
		}
	}, [showCanvas, formValues]);

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<Formik
					enableReinitialize={true}
					initialValues={initFormValues}
					validationSchema={Yup.object().shape({ ...Section80GGADonationFormSchema })}
					onSubmit={(values) => handleCloseBtnPress(values)}
				>
					{({
						handleSubmit,
						handleBlur,
						submitCount,
						isValid,
						errors,
						values,
						touched,
						setFieldValue
					}) => (
						<>
							<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

							<Offcanvas.Header>
								<Offcanvas.Title>
									Donations for Scientific Research or Rural Development - Section 80GGA
								</Offcanvas.Title>
								<Button
									variant="primary"
									className="icon-only hide-offcanvas"
									onClick={() => handleCloseBtnPress(null)}
								>
									<FASvgIcon icon={faXmark} />
								</Button>
							</Offcanvas.Header>

							<Offcanvas.Body>
								<section className="mb-5">
									<div className="section-title-wrap">
										<h5>Clause Details</h5>
									</div>
									<FormField label="Clause Name" isRequired={false} sm={6}>
										<SelectInputField
											placeholder="Select"
											name="DonationCause"
											options={UtilService.convertToArray(RelevantClauseOptions)}
											value={{
												"label": RelevantClauseOptions[values.DonationCause],
												"value": values.DonationCause
											}}
											onChange={(selectedOption) => {
												if (selectedOption) {
													setFieldValue(`DonationCause`, selectedOption.value);
												} else {
													setFieldValue(`DonationCause`, undefined);
												}
											}}
											onBlur={handleBlur(`DonationCause`)}
											isInvalid={
												Boolean(getIn(errors, `DonationCause`)) && getIn(touched, `DonationCause`)
											}
											errorMsg={getIn(errors, `DonationCause`)}
											note="(8 Causes) If one of these is selected, most of the details are filled automatically"
										/>
									</FormField>

									<FormField label="PAN of Organization" isRequired={false} sm={6}>
										<TextInputField
											name={`DoneePAN`}
											value={values.DoneePAN}
											onChange={(value) => setFieldValue(`DoneePAN`, value.toUpperCase())}
											onBlur={handleBlur(`DoneePAN`)}
											isInvalid={Boolean(getIn(errors, `DoneePAN`)) && getIn(touched, `DoneePAN`)}
											errorMsg={getIn(errors, `DoneePAN`)}
											note="Enter 'GGGGG0000G' if you have donated to Government Funds that don't have a PAN"
										/>
									</FormField>

									<FormField label="Name of the Organization" isRequired={false} sm={6}>
										<TextInputField
											name={`DoneeName`}
											value={values.DoneeName}
											onChange={(value) => setFieldValue(`DoneeName`, value)}
											onBlur={handleBlur(`DoneeName`)}
											isInvalid={Boolean(getIn(errors, `DoneeName`)) && getIn(touched, `DoneeName`)}
											errorMsg={getIn(errors, `DoneeName`)}
										/>
									</FormField>
								</section>
								<section className="mb-5">
									<div className="section-title-wrap">
										<h5>Organization Address</h5>
									</div>
									<FormField label="Full Address" isRequired={true}>
										<TextInputField
											name="FullAddress"
											value={values.FullAddress}
											onChange={(value) => setFieldValue("FullAddress", value)}
											onBlur={handleBlur("FullAddress")}
											isInvalid={Boolean(errors.FullAddress) && touched.FullAddress}
											errorMsg={errors.FullAddress}
										/>
									</FormField>

									<FormField label="Pincode/Zipcode" isRequired={true}>
										<NumericInputField
											name="PinCode"
											value={values.PinCode}
											onChange={async (value) => {
												setFieldValue("PinCode", Number(value));
												setFieldValue("ZipCode", value);

												if (value.length === 6) {
													const data = await UtilService.getStateCityFromPincode(value);
													if (data) {
														setFieldValue("CountryCode", data.CountryCode);
														setFieldValue("StateCode", data.StateCode);
														setFieldValue("CityOrTownOrDistrict", data.CityOrTownOrDistrict);
													} else {
														setFieldValue("CountryCode", "");
														setFieldValue("StateCode", "");
														setFieldValue("CityOrTownOrDistrict", "");
													}
												}
											}}
											onBlur={handleBlur("PinCode")}
											isInvalid={Boolean(errors.PinCode) && touched.PinCode}
											errorMsg={errors.PinCode}
											decimalUpto={0}
											separator=""
										/>
									</FormField>

									<FormField label="Country | State | City" isRequired={true} sm={3}>
										<Row className="align-items-center">
											<Col sm={4}>
												<SelectInputField
													placeholder="Select Country"
													name="CountryCode"
													label="Country"
													isSearchable={true}
													isOptionArrayOfObject={true}
													options={UtilService.convertToArray(CountryWithCode)}
													value={{
														"label": CountryWithCode[values.CountryCode],
														"value": values.CountryCode
													}}
													onChange={(selectedOption) => {
														if (selectedOption) {
															setFieldValue("CountryCode", selectedOption.value);
															if (selectedOption.value !== "91") {
																setFieldValue("StateCode", "99");
															} else {
																setFieldValue("StateCode", "");
															}
														} else {
															setFieldValue("CountryCode", undefined);
														}
													}}
													onBlur={handleBlur("CountryCode")}
													isInvalid={Boolean(errors.CountryCode) && touched.CountryCode}
													errorMsg={errors.CountryCode}
												/>
											</Col>
											<Col sm={4}>
												<SelectInputField
													placeholder="Select state"
													name="StateCode"
													label="State"
													isSearchable={true}
													isOptionArrayOfObject={true}
													options={UtilService.convertToArray(StateWithCode)}
													value={{
														"label": StateWithCode[values.StateCode],
														"value": values.StateCode
													}}
													onChange={(selectedOption) => {
														if (selectedOption) {
															setFieldValue("StateCode", selectedOption.value);
														} else {
															setFieldValue("StateCode", undefined);
														}
													}}
													onBlur={handleBlur("StateCode")}
													isInvalid={Boolean(errors.StateCode) && touched.StateCode}
													errorMsg={errors.StateCode}
												/>
											</Col>
											<Col sm={4}>
												<TextInputField
													name="CityOrTownOrDistrict"
													label="City"
													value={values.CityOrTownOrDistrict}
													onChange={(value) => setFieldValue("CityOrTownOrDistrict", value)}
													onBlur={handleBlur("CityOrTownOrDistrict")}
													isInvalid={
														Boolean(errors.CityOrTownOrDistrict) && touched.CityOrTownOrDistrict
													}
													errorMsg={errors.CityOrTownOrDistrict}
												/>
											</Col>
										</Row>
									</FormField>
								</section>
								<section className="mb-5">
									<div className="section-title-wrap">
										<h5>Donation Details</h5>
									</div>
									<FormField label="Donation Amount in Cash" isRequired={true} sm={6}>
										<NumericInputField
											showLeftIcon={true}
											name="CashDonation"
											value={values.CashDonation}
											onChange={(value) => {
												setFieldValue("CashDonation", Number(value));
												const sum = Number(values?.NonCashDonation || 0) + Number(value || 0);
												setFieldValue("TotalDonation", sum);
											}}
											onBlur={handleBlur("CashDonation")}
											isInvalid={Boolean(errors.CashDonation) && touched.CashDonation}
											errorMsg={errors.CashDonation}
										/>
									</FormField>
									<FormField
										label="Donation Amount in Online/Cheque/UPI/NEFT"
										isRequired={true}
										sm={6}
									>
										<NumericInputField
											showLeftIcon={true}
											name="NonCashDonation"
											value={values.NonCashDonation}
											onChange={(value) => {
												setFieldValue("NonCashDonation", Number(value));
												const sum = Number(values?.CashDonation || 0) + Number(value || 0);
												setFieldValue("TotalDonation", sum);
											}}
											onBlur={handleBlur("NonCashDonation")}
											isInvalid={Boolean(errors.NonCashDonation) && touched.NonCashDonation}
											errorMsg={errors.NonCashDonation}
										/>
									</FormField>
								</section>
							</Offcanvas.Body>

							<div className="offcanvas-btns">
								{!isFilingReadOnly ? (
									<Button variant="outline-white" size="sm" onClick={() => handleSubmit()}>
										Save and Close
									</Button>
								) : (
									<Button
										variant="outline-white"
										size="sm"
										onClick={() => handleCloseBtnPress(null)}
									>
										Close
									</Button>
								)}
							</div>
						</>
					)}
				</Formik>
			</Offcanvas>
		</>
	);
};

export default DonationUnderSection80GGAForm;
